<template>
  <slide-out
    dock="right"
    :size="sideOutCurrentSize"
    :min-size="sideOutMinSize"
    id="task-filters-slideout"
    ref="taskSlideout"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    :style="besideAssistantStyle"
    :visible.sync="slideouts.active"
    v-on:close="slideoutClose"
    @resize="OnResize">
    <template v-slot:header>
      <div class="task-filters-header d-flex align-center justify-space-between">
        <h3 class="font-weight-bold d-flex align-center mb-0">
          <v-icon left color="primary">mdi-filter</v-icon> Filters
        </h3>
        <v-btn @click="slideoutClose" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </template>

    <div class="task-filters-body">
      <!-- Selected summary: one removable chip per selected value, grouped by filter. -->
      <div v-if="activeGroups.length" class="task-filters-summary">
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="text-caption grey--text">{{ totalActiveCount }} active</span>
          <v-btn text x-small color="error" @click="resetAllFilters">Reset all</v-btn>
        </div>
        <div class="summary-chips">
          <div v-for="group in activeGroups" :key="group.prop" class="summary-group mb-1">
            <span
              class="summary-group-label text-caption grey--text text--darken-2 font-weight-medium">
              {{ group.filterLabel }}:
            </span>
            <v-chip
              v-for="v in group.values"
              :key="`${group.prop}:${v.id}`"
              small
              label
              outlined
              color="primary"
              close
              class="ml-1 mb-1 task-filter-chip"
              :close-icon-aria-label="`Remove ${group.filterLabel} ${v.valueName}`"
              @click:close="removeChip({ prop: group.prop, id: v.id })">
              {{ v.valueName }}
            </v-chip>
          </div>
        </div>
      </div>

      <!-- Filter-name search: client-side narrowing of the accordions by filter
           name. No fetch. Shown only when there are enough filters to warrant it. -->
      <div v-if="filters.length > 8" class="task-filters-search">
        <v-text-field
          v-model="filterNameQuery"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          placeholder="Search filters by name"
          aria-label="Search filters by name" />
      </div>

      <!-- Category accordions (multi-open). Categories come from the task type's
           sections (+ a synthetic "General" group for the base filters). -->
      <div class="task-filters-scroll">
        <v-expansion-panels accordion flat multiple v-model="expandedPanels">
          <v-expansion-panel
            v-for="category in categories"
            :key="category.name"
            v-show="isCategoryVisible(category)">
            <v-expansion-panel-header>
              <template v-slot:default>
                <div class="d-flex align-center justify-space-between w-100">
                  <span
                    :class="{
                      'primary--text font-weight-medium': categoryActiveCount(category) > 0,
                    }">
                    {{ category.name }}
                  </span>
                  <div class="d-flex align-center">
                    <v-progress-circular
                      v-if="loadingCategories[category.name]"
                      indeterminate
                      color="primary"
                      size="14"
                      width="2"
                      class="mr-2" />
                    <v-chip
                      v-if="categoryActiveCount(category) > 0"
                      x-small
                      color="primary"
                      class="mr-2"
                      aria-hidden="true">
                      {{ categoryActiveCount(category) }}
                    </v-chip>
                    <v-btn
                      v-if="categoryActiveCount(category) > 0"
                      icon
                      x-small
                      class="mr-2"
                      :aria-label="`Clear all ${category.name} filters`"
                      @click.stop="clearCategory(category)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="filter in visibleFiltersFor(category)" :key="filter.prop" class="mb-3">
                <div
                  :id="captionIdFor(filter)"
                  class="text-caption grey--text text--darken-2 font-weight-medium mb-1">
                  {{ filter.label }}
                </div>
                <div v-if="loadingCategories[category.name] && filter.options === undefined">
                  <v-skeleton-loader type="text" />
                </div>
                <div
                  v-else-if="filter.options && filter.options.length === 0"
                  class="text-caption grey--text font-italic">
                  no values
                </div>
                <general-selector
                  v-else
                  :items="filter.options || []"
                  :item-text="itemTextFor(filter)"
                  :item-value="filter.itemValue || 'id'"
                  :value="taskOptions[filter.prop] || []"
                  :placeholder="`Search ${filter.label}`"
                  :aria-labelledby="captionIdFor(filter)"
                  no-data-text="No matching values"
                  :default-style="{ outlined: true, clearable: true }"
                  :menu-props="{ offsetY: true, zIndex: 2100, maxHeight: 320 }"
                  @input="setFilterValue(filter, $event)" />
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Empty state: no filters at all -->
        <div v-if="filters.length === 0" class="text-center py-8">
          <v-icon size="48" color="grey lighten-1">mdi-filter-off-outline</v-icon>
          <p class="text-body-2 grey--text mt-2">No filters available</p>
        </div>
        <!-- Empty state: filters exist but none match the name search -->
        <div v-else-if="normalizedQuery && !hasVisibleCategories" class="text-center py-8">
          <v-icon size="48" color="grey lighten-1">mdi-filter-off-outline</v-icon>
          <p class="text-body-2 grey--text mt-2">No filters match “{{ filterNameQuery }}”</p>
        </div>
      </div>
    </div>
  </slide-out>
</template>

<script>
  import generalSelector from '@Share/components/generalSelector.vue';
  import httpService from '@Utils/httpService';
  import { error as debugError } from '@/utils/debugLogger';
  import { besidePanelStyle } from '@Assistant/domain/besidePanel';

  export default {
    name: 'edit-task',
    data() {
      return {
        slideouts: {
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
        // Flat filter list (from open()); each carries label, prop, url, category,
        // fieldId (custom), and a lazily-populated `options` array.
        filters: [],
        // Open category indices (multi-open accordion).
        expandedPanels: [],
        // { [categoryName]: true } while that category's filters load.
        loadingCategories: {},
        // { [prop]: { [valueId]: displayName } } — captured as the user selects,
        // so the summary can show value NAMES with no extra fetch. Persists across
        // opens; entries are keyed by globally-unique value ids so there's no
        // cross-board collision.
        labelMap: {},
        // Client-side filter-name search query (narrows visible accordions; no fetch).
        filterNameQuery: '',
      };
    },
    components: {
      generalSelector,
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      taskTypes() {
        return this.$store.getters['TASK_MANAGER/GET_ENTRIES'];
      },
      taskTypeId() {
        return this.$route.params.taskTypeId;
      },
      taskType() {
        return (
          this.taskTypes.find((e) => e.id == this.taskTypeId) || { sections: [], statuses: [] }
        );
      },
      fields() {
        return (this.taskType.sections || []).flatMap((section) => section.fields);
      },
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      /** Beside the AVA side panel, not over it, so the chat stays usable while filtering. */
      besideAssistantStyle() {
        return besidePanelStyle({ nested: this.nested });
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
      taskOptions: {
        get() {
          return this.$store.getters['TASK/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('TASK/SET_OPTIONS', val);
        },
      },
      // Group the flat filters by `category`, "General" first, then sections in
      // their natural (insertion) order.
      categories() {
        const byName = {};
        const groups = [];
        this.filters.forEach((f) => {
          const name = f.category || 'General';
          if (!byName[name]) {
            byName[name] = { name, filters: [] };
            groups.push(byName[name]);
          }
          byName[name].filters.push(f);
        });
        groups.sort((a, b) => (a.name === 'General' ? -1 : b.name === 'General' ? 1 : 0));
        return groups;
      },
      // Selected values grouped by filter: [{ prop, filterLabel, values:[{id,valueName}] }].
      activeGroups() {
        const opts = this.taskOptions || {};
        const groups = [];
        this.filters.forEach((f) => {
          const vals = opts[f.prop];
          if (vals && vals.length) {
            groups.push({
              prop: f.prop,
              filterLabel: f.label,
              values: vals.map((id) => ({
                id,
                valueName: (this.labelMap[f.prop] && this.labelMap[f.prop][id]) || id,
              })),
            });
          }
        });
        return groups;
      },
      totalActiveCount() {
        return this.activeGroups.reduce((sum, g) => sum + g.values.length, 0);
      },
      // Lower-cased, trimmed filter-name query ('' = no narrowing).
      normalizedQuery() {
        return (this.filterNameQuery || '').trim().toLowerCase();
      },
      // True when at least one category is visible under the current query.
      hasVisibleCategories() {
        return this.categories.some((c) => this.isCategoryVisible(c));
      },
    },
    watch: {
      // Load a category's filter options when it is opened (lazy). Idempotent —
      // loadCategory skips already-loaded filters.
      expandedPanels(openIndices) {
        (openIndices || []).forEach((i) => {
          const category = this.categories[i];
          if (category) this.loadCategory(category);
        });
      },
      // Filter-name search narrows only what's VISIBLE (categories use v-show, so
      // panel indices never shift against `categories`). This watcher just re-derives
      // which panels are open for UX: expand every matching category while searching,
      // restore the default expansion when the query clears. Purely cosmetic — it can
      // never change which category a given index maps to.
      filterNameQuery() {
        if (this.normalizedQuery) {
          this.expandedPanels = this.categories
            .map((c, i) => i)
            .filter((i) => this.isCategoryVisible(this.categories[i]));
        } else {
          this.expandedPanels = this.computeDefaultExpansion();
        }
      },
      nested: {
        handler() {
          this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
          this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
        },
        deep: true,
      },
    },
    methods: {
      slideoutClose() {
        this.slideouts.active = false;
      },
      OnResize() {
        let TaskFiltersSlideOut = this.$refs.taskSlideout;
        this.sideOutCurrentSize = TaskFiltersSlideOut.resizeValue;
      },
      open(filters) {
        // Preserve loaded options ONLY within the same board (avoids refetch on
        // re-open); on a board change, start fresh so we never show the previous
        // board's option lists.
        const boardChanged = this._lastBoardId !== this.taskTypeId;
        this._lastBoardId = this.taskTypeId;
        if (!boardChanged && this.filters.length) {
          const prev = new Map(this.filters.map((f) => [f.prop, f.options]));
          this.filters = filters.map((f) => ({ ...f, options: prev.get(f.prop) }));
        } else {
          this.filters = filters.map((f) => ({ ...f }));
        }
        // Always open with a clear search; auto-expand categories that already
        // have active selections (else "General"). Setting expandedPanels triggers
        // the watcher, which lazy-loads each opened category.
        this.filterNameQuery = '';
        this.expandedPanels = this.computeDefaultExpansion();
        this.slideouts.active = true;
      },
      // Indices (into `categories`) to expand by default: every category with an
      // active selection, or just the first ("General") when none are active.
      computeDefaultExpansion() {
        const cats = this.categories;
        const activeIdx = cats
          .map((c, i) => i)
          .filter((i) => cats[i].filters.some((f) => (this.taskOptions[f.prop] || []).length > 0));
        return activeIdx.length ? activeIdx : cats.length ? [0] : [];
      },
      // Filters within a category that match the current name query. No query →
      // all of them; a category-name match → all of them; otherwise label match.
      visibleFiltersFor(category) {
        const q = this.normalizedQuery;
        if (!q) return category.filters;
        if (category.name.toLowerCase().includes(q)) return category.filters;
        return category.filters.filter((f) => (f.label || '').toLowerCase().includes(q));
      },
      // A category is shown (v-show) when it has ≥1 matching filter, or no query.
      isCategoryVisible(category) {
        if (!this.normalizedQuery) return true;
        return this.visibleFiltersFor(category).length > 0;
      },
      // DOM-id-safe id for a filter's caption, so the autocomplete can reference it
      // via aria-labelledby. `prop` is unique per filter (e.g. "customFields[5]"),
      // sanitized to valid id chars.
      captionIdFor(filter) {
        return `tf-cap-${String(filter.prop).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
      },
      async loadCategory(category) {
        const toLoad = category.filters.filter((f) => f.options === undefined);
        if (!toLoad.length) return;
        this.$set(this.loadingCategories, category.name, true);
        try {
          await Promise.all(toLoad.map((f) => this.loadSingleFilterOptions(f)));
        } finally {
          this.$set(this.loadingCategories, category.name, false);
        }
      },
      async loadSingleFilterOptions(item) {
        try {
          const res = await httpService.get({ url: item.url });
          let options = res.data.item || res.data;

          // Custom-field options → ensure each has a display value. `item.fieldId`
          // is the stable raw field id; `prop` is already the final data key.
          const fieldOptions = item.fieldId ? this.fields.find((f) => f.id === item.fieldId) : null;
          if (fieldOptions) {
            const defs = fieldOptions.options || [];
            options = options.map((o) => {
              const def = defs.find((d) => d.id === o.id);
              return { ...o, displayValue: def ? def.value : o.displayValue ?? o.name ?? o.id };
            });
          }

          this.$set(item, 'options', options);
        } catch (err) {
          // Degrade to an empty option list so one failing filter doesn't break the
          // whole panel — but LOG so a real failure (500 / network) isn't silently
          // indistinguishable from a genuinely empty filter.
          debugError('Failed to load task filter options', { url: item.url, err });
          this.$set(item, 'options', []);
        }
      },
      itemTextFor(filter) {
        if (filter.type === 'userList') return 'firstName';
        if (filter.fieldId) return 'displayValue';
        return filter.itemText || 'name';
      },
      setFilterValue(filter, ids) {
        // Capture id → display-name for the summary chips (no extra fetch).
        const valueKey = filter.itemValue || 'id';
        const textKey = this.itemTextFor(filter);
        const map = { ...(this.labelMap[filter.prop] || {}) };
        (filter.options || []).forEach((o) => {
          if (ids.includes(o[valueKey])) map[o[valueKey]] = o[textKey] ?? o[valueKey];
        });
        this.$set(this.labelMap, filter.prop, map);

        this.taskOptions = { ...this.taskOptions, [filter.prop]: ids };
      },
      removeChip(chip) {
        const current = this.taskOptions[chip.prop] || [];
        this.taskOptions = {
          ...this.taskOptions,
          [chip.prop]: current.filter((id) => id !== chip.id),
        };
      },
      clearCategory(category) {
        const next = { ...this.taskOptions };
        category.filters.forEach((f) => {
          next[f.prop] = [];
        });
        this.taskOptions = next;
      },
      categoryActiveCount(category) {
        return category.filters.filter((f) => (this.taskOptions[f.prop] || []).length > 0).length;
      },
      resetAllFilters() {
        this.taskOptions = {
          ...this.taskOptions,
          ...Object.fromEntries(Object.keys(this.taskOptions).map((key) => [key, []])),
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .task-filters-header {
    width: 100%;
    padding: 10px 16px;
    border-bottom: 1px solid var(--v-light-grey-base);
  }
  .task-filters-body {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .task-filters-summary {
    flex: 0 0 auto;
    padding: 8px 12px;
    border-bottom: 1px solid var(--v-light-grey-base);
  }
  .task-filters-search {
    flex: 0 0 auto;
    padding: 8px 12px;
    border-bottom: 1px solid var(--v-light-grey-base);
  }
  .summary-chips {
    max-height: 120px;
    overflow-y: auto;
  }
  .summary-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .summary-group-label {
    white-space: nowrap;
  }
  .task-filters-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 12px;
  }
  .v-expansion-panel {
    margin-bottom: 10px;
    border: 1px solid var(--v-light-grey-base);
    .v-expansion-panel-header,
    .v-expansion-panel-content {
      border-right: 1px solid var(--v-light-grey-base);
      border-left: 1px solid var(--v-light-grey-base);
    }
    .v-expansion-panel-header {
      border-top: 1px solid var(--v-light-grey-base);
      border-bottom: 1px solid var(--v-light-grey-base);
    }
    .v-expansion-panel-header--active {
      border-bottom: none;
    }
    .v-expansion-panel-content {
      border-bottom: 1px solid var(--v-light-grey-base);
    }
  }
</style>

<style lang="scss">
  /* Un-scoped: the slide-out is teleported to #app, so scoped styles can't reach
     the library's header wrapper. A unique id (not the shared "edit-task-slideout")
     stops EditTask's padding from leaking; here we neutralize the library's default
     header padding so .task-filters-header owns the spacing. */
  #task-filters-slideout {
    .vue-slideout-header {
      padding: 0 !important;
    }
    .task-filters-header {
      padding: 10px 16px;
    }
  }
</style>
